import { AbstractControl, ValidatorFn } from '@angular/forms'
import { Dictionary, DirectusFiles, DirectusUsers, EnglishTerm, GameMap, Level, Term } from 'src/types'
import { GameType, HelpText } from '../game/game-model'

export enum DictionaryUserStatus {
  Pending = 'Pending',
  Connected = 'Connected',
  Withdrawn = 'Withdrawn',
  Declined = 'Declined'
}

export const UsersDictionaryCollection = 'junction_directus_users_dictionary'

export enum DictionaryStatus {
  Published = 'Published',
  Draft = 'Draft'
}

export enum GameMapStatus {
  Published = 'Published',
  Draft = 'Draft'
}

export interface ManagementDictionary extends Dictionary {
  isSelected: boolean
  terms: Term[]
  gameMapsAmount?: number
  mediaAmount?: number
  achievementsAmount?: number
  collectablesAmount?: number
  hasPublishedGames?: boolean
}

export interface DictionaryForm {
  isSelected: boolean
  dictionary: ManagementDictionary
}

export interface ManagementWord extends Term {
  application?: EnglishTerm
  isSelected: boolean
  isUsedInGame?: boolean
}

export interface WordForm {
  isSelected: boolean
  word: ManagementWord
}

export interface ManagementUser extends DirectusUsers {
  isSelected: boolean
  dictionaryStatus: DictionaryUserStatus
  connection: Dictionary
  recordId: string
  name: string
}

export interface UserForm {
  isSelected: boolean
  user: ManagementUser
}

export interface ManagementMedia extends DirectusFiles {
  isSelected: boolean
  isUsed?: boolean
}

export interface MediumForm {
  isSelected: boolean
  medium: ManagementMedia
}

export const wordDuplicateValidator =
  (words: string[]): ValidatorFn =>
    (control: AbstractControl) => {
      return words.includes(control.value.toLowerCase()) ? { duplicated: true } : null
    }

export interface ManagementGameMap extends GameMap {
  isSelected?: boolean
}

export interface MapForm {
  isSelected: boolean
  map: ManagementGameMap
}

export interface ManagementLevel extends Level {
  isSelected: boolean
}

export interface LevelForm {
  isSelected: boolean
  level: ManagementLevel
}

export const BuiltInGames: BuiltInGame[] = [
  {
    name: GameType.StoryFill,
    icon: 'game-icon-story-fill'
  },
  {
    name: GameType.BodyParts,
    icon: 'game-icon-body-parts-game'
  },
  {
    name: GameType.BodyPartsPrefilled,
    icon: 'game-icon-body-parts-prefilled'
  },
  {
    name: GameType.DragImage,
    icon: 'game-icon-drag-image-on-word'
  },
  {
    name: GameType.MatchOriginalTermWithImage,
    icon: 'game-icon-match-original-term-with-image'
  },
  {
    name: GameType.MatchPairs,
    icon: 'game-icon-match-pairs'
  },
  {
    name: GameType.SceneDragAndDrop,
    icon: 'game-icon-scene-drag-drop'
  },
  {
    name: GameType.SelectItem,
    icon: 'game-icon-select-item'
  },
  {
    name: GameType.SelectWord,
    icon: 'game-icon-select-word'
  },
  {
    name: GameType.SelectImageForWord,
    icon: 'game-icon-select-image-for-word'
  },
  {
    name: GameType.SelectWordForImage,
    icon: 'game-icon-select-word-for-image'
  },
  {
    name: GameType.Sentence,
    icon: 'game-icon-sentence'
  }
]

export enum BuiltInSideActivityType {
  Video = 'Video',
  SingWithMe = 'Sing with Me',
  Collectable = 'Collectable'
}

export const BuiltInSideActivities = [
  {
    name: BuiltInSideActivityType.Video,
    icon: 'side-activity-video'
  },
  {
    name: BuiltInSideActivityType.SingWithMe,
    icon: 'side-activity-sing-with-me'
  },
  {
    name: BuiltInSideActivityType.Collectable,
    icon: 'side-activity-collectable'
  }
]

export interface BuiltInSideActivity {
  name: BuiltInSideActivityType
  icon: string
}

export interface BuiltInGame {
  name: GameType | BuiltInSideActivityType
  icon: string
}

export interface EditableGame {
  id: string
  title: string
  icon: string
  words: string[]
  order: number
}

export enum AsideMenuId {
  DictionaryMenu = 'dictionary-menu',
  AddGame = 'add-game',
  AddSideActivity = 'add-side-activity',
  AddAchievement = 'add-achievement',
  AddCollectable = 'add-collectable'
}

export enum AsideMenuContentId {
  GameMenuContent = 'game-menu-content',
  SideActivityMenuContent = 'side-activity-menu-content',
  DictionaryMenuContent = 'dictionary-menu-content',
  AchievementMenuContent = 'achievement-menu-content'
}

export enum ContentManagerSections {
  ContentAdmin = 'content-admin',
  DictionaryAdmin = 'dictionary-admin',
  MapBuilder = 'map-builder',
  UsersAdmin = 'users-admin',
  DictionarySingle = 'single-dictionary',
  GameMaps = 'game-maps',
  GameMapLevels = 'game-map-levels',
  MapLevelSingle = 'single-map-level',
  GameSequenceBuilder = 'game-sequence-builder',
  Media = 'media',
  Collectables = 'collectables',
  Achievements = 'achievements',
  AchievementSingle = 'single-achievement',
  CollectableSingle = 'single-collectable'
}


export const CMHelpText: Record<ContentManagerSections, HelpText> = {
  [ContentManagerSections.ContentAdmin]: {
    title: `Content Screen`,
    body: `Add images and sounds in Media or create achievements and collectables that you can later use when building your game map and dictionaries.`
  },
  [ContentManagerSections.DictionaryAdmin]: {
    title: `Dictionary Admin`,
    body: `Create a new dictionary or edit an existing one. You will need a dictionary before you start building your games in the Map Builder.`
  },
  [ContentManagerSections.MapBuilder]: {
    title: `Map Builder`,
    body: `Choose a Language to begin building a Game Map for that Language or to edit an existing map.`
  },
  [ContentManagerSections.UsersAdmin]: {
    title: `Users Admin`,
    body: `Control who connects to your languages. Add and accept users. Withdraw or remove users.`
  },
  [ContentManagerSections.DictionarySingle]: {
    title: `Your Dictionary`,
    body: `Add words to your dictionary. Each word should contain an image that represents the word and an audio clip of how to say the word. You'll need words in your dictionary to start building your games.`
  },
  [ContentManagerSections.GameMaps]: {
    title: `Game Maps`,
    body: `Create and edit game maps for this language. Create as many game maps as you like.`
  },
  [ContentManagerSections.GameMapLevels]: {
    title: `Game Map Levels`,
    body: `Add levels to your game map. You need to add a level to start building games. You can add as many levels as you like.`
  },
    [ContentManagerSections.MapLevelSingle]: {
    title: `Map Level`,
    body: `Add games and side activities to the map circles and side dots. Just press on any marker with a plus.`
  },
    [ContentManagerSections.GameSequenceBuilder]: {
    title: `Game Sequence Builder`,
    body: `Add games to create a game sequence. You can add as many games in your sequence as you like.`
  },
  [ContentManagerSections.Media]: {
    title: `Media`,
    body: `Upload images and sounds to use in your games and dictionaries.`
  },
  [ContentManagerSections.Collectables]: {
    title: `Collectables`,
    body: `Create a collection of images that can be collected as players progress through the game map.`
  },
  [ContentManagerSections.Achievements]: {
    title: `Achievements`,
    body: `Achievements you create here will appear as goals for players to complete in the Learning Achievements screen.`
  },
  [ContentManagerSections.AchievementSingle]: {
    title: `Creating an Achievement`,
    body: `Set a target number for each star level (up to 5 stars) and add a cover image to display on the badge in the player's Learning Achievements screen.`
  },
  [ContentManagerSections.CollectableSingle]: {
    title: `Creating a Collectable`,
    body: `Add images to your collection and add a cover image to display on the badge in the player's Learning Achievements screen.`
  }
}
